import { useState, useEffect, useCallback } from 'react';
import { adminSeoTasksAPI } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Loader2, Plus, CheckCircle2, Clock, AlertCircle, Trash2, Edit3, Search,
  TrendingUp, ListChecks, ChevronRight, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_COLORS = {
  high: 'border-red-500/40 text-red-400',
  medium: 'border-amber-500/40 text-amber-400',
  low: 'border-slate-500/40 text-slate-400',
};

const STATUS_COLORS = {
  pending: 'border-slate-500/40 text-slate-400',
  in_progress: 'border-amber-500/40 text-amber-400',
  done: 'border-green-500/40 text-green-400',
};

export default function AdminSeoPage() {
  const [tab, setTab] = useState('health');
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const r = await adminSeoTasksAPI.list();
      setTasks(r.data || []);
    } catch (e) { toast.error('Failed to load tasks'); }
    finally { setTasksLoading(false); }
  }, []);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const r = await adminSeoTasksAPI.health();
      setHealth(r.data);
    } catch (e) { /* ignore */ }
    finally { setHealthLoading(false); }
  }, []);

  useEffect(() => { loadTasks(); loadHealth(); }, [loadTasks, loadHealth]);

  const openNew = () => {
    setEditing({ title: '', description: '', instructions: '', priority: 'medium', status: 'pending', category: 'general' });
    setOpen(true);
  };

  const openEdit = (t) => { setEditing({ ...t }); setOpen(true); };

  const save = async () => {
    if (!editing.title) { toast.error('Title is required'); return; }
    try {
      if (editing.id) {
        await adminSeoTasksAPI.update(editing.id, editing);
        toast.success('Updated');
      } else {
        await adminSeoTasksAPI.create(editing);
        toast.success('Task created');
      }
      setOpen(false);
      loadTasks();
    } catch (e) { toast.error('Save failed'); }
  };

  const setStatus = async (task, status) => {
    try {
      await adminSeoTasksAPI.update(task.id, { ...task, status });
      loadTasks();
      loadHealth();
    } catch (e) { toast.error('Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await adminSeoTasksAPI.delete(id);
    loadTasks();
  };

  const grouped = tasks.reduce((acc, t) => {
    const k = t.category || 'general';
    (acc[k] = acc[k] || []).push(t);
    return acc;
  }, {});

  const completed = tasks.filter(t => t.status === 'done').length;
  const total = tasks.length;

  return (
    <div className="space-y-6" data-testid="admin-seo-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-amber-400" /> Advanced SEO
          </h1>
          <p className="text-sm text-slate-400 mt-1">Health checks + actionable guidance for tasks AI can&apos;t automate.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0B1221] border border-white/5">
          <TabsTrigger value="health" data-testid="tab-health">Health Check</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">
            Guidance Tasks ({completed}/{total})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="space-y-4 mt-6">
          {healthLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
          ) : health ? (
            <>
              <div className="bg-gradient-to-br from-amber-500/10 to-purple-500/5 border border-amber-500/20 rounded-2xl p-8 text-center">
                <div className="text-6xl font-bold text-white" data-testid="seo-score">
                  {health.score}<span className="text-2xl text-slate-500">/100</span>
                </div>
                <p className="text-amber-400 mt-2 font-medium">SEO Health Score</p>
                <div className="mt-4 h-3 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-500"
                    style={{ width: `${health.score}%` }} />
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-3">
                <StatCard label="Total Posts" value={health.stats.posts_total} />
                <StatCard label="Published" value={health.stats.posts_published} />
                <StatCard label="AI-Generated" value={health.stats.ai_posts} />
                <StatCard label="Missing Meta" value={health.stats.posts_missing_meta} alert={health.stats.posts_missing_meta > 0} />
              </div>

              <div className="bg-[#0B1221] border border-white/5 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><ListChecks className="w-4 h-4" /> Health Checks</h3>
                <div className="space-y-2">
                  {health.checks.map((c, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg" data-testid={`check-${i}`}>
                      {c.passed
                        ? <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        : <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1">
                        <p className={`text-sm ${c.passed ? 'text-slate-300' : 'text-white'}`}>{c.name}</p>
                        {!c.passed && c.hint && <p className="text-xs text-slate-500 mt-1">{c.hint}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4 mt-6">
          <div className="flex justify-end">
            <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="new-task-btn">
              <Plus className="w-4 h-4 mr-2" /> New Task
            </Button>
          </div>

          {tasksLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat}>
                  <h4 className="text-sm uppercase tracking-widest text-slate-500 mb-2">{cat}</h4>
                  <div className="space-y-2">
                    {list.map(t => (
                      <div key={t.id} className="bg-[#0B1221] border border-white/5 rounded-xl overflow-hidden" data-testid={`task-${t.id}`}>
                        <div className="p-4 flex items-start gap-3">
                          <button
                            onClick={() => setStatus(t, t.status === 'done' ? 'pending' : 'done')}
                            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              t.status === 'done' ? 'bg-green-500 border-green-500' : 'border-slate-500 hover:border-amber-500'
                            }`}
                            data-testid={`toggle-${t.id}`}
                          >
                            {t.status === 'done' && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <Badge variant="outline" className={PRIORITY_COLORS[t.priority] || ''}>{t.priority}</Badge>
                              <Badge variant="outline" className={STATUS_COLORS[t.status] || ''}>{(t.status || '').replace('_', ' ')}</Badge>
                            </div>
                            <h5 className={`font-medium ${t.status === 'done' ? 'text-slate-500 line-through' : 'text-white'}`}>{t.title}</h5>
                            {t.description && <p className="text-sm text-slate-400 mt-1">{t.description}</p>}
                            <button
                              onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                              className="text-xs text-amber-400 hover:text-amber-300 mt-2 flex items-center gap-1"
                              data-testid={`expand-${t.id}`}
                            >
                              <ChevronRight className={`w-3 h-3 transition-transform ${expanded === t.id ? 'rotate-90' : ''}`} />
                              Step-by-step instructions
                            </button>
                            {expanded === t.id && t.instructions && (
                              <div className="mt-3 p-3 bg-[#020617] rounded-lg border border-white/5">
                                <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans">{t.instructions}</pre>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                              <SelectTrigger className="h-8 w-32 text-xs bg-[#020617] border-white/10"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="done">Done</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-slate-500 hover:text-amber-400 h-8" data-testid={`edit-task-${t.id}`}>
                              <Edit3 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(t.id)} className="text-slate-500 hover:text-red-400 h-8">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B1221] border-white/10 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white">{editing?.id ? 'Edit Task' : 'New SEO Task'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Title *</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="bg-[#020617] border-white/10 text-white" data-testid="task-title" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="bg-[#020617] border-white/10 text-white" />
              </div>
              <div>
                <Label>Step-by-step instructions</Label>
                <Textarea rows={6} value={editing.instructions} onChange={(e) => setEditing({ ...editing, instructions: e.target.value })}
                  placeholder="Use line breaks for steps. Markdown links supported via plain text."
                  className="bg-[#020617] border-white/10 text-white text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>Priority</Label>
                  <Select value={editing.priority} onValueChange={(v) => setEditing({ ...editing, priority: v })}>
                    <SelectTrigger className="bg-[#020617] border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="bg-[#020617] border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="bg-[#020617] border-white/10 text-white" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="save-task-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, alert }) {
  return (
    <div className={`p-4 rounded-xl border ${alert ? 'border-red-500/30 bg-red-500/5' : 'border-white/5 bg-[#0B1221]'}`}>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
}
